/**
 * VesselPicker - Wrapper around generic EntityPicker
 * 
 * Provides vessel-specific configuration and maintains backward compatibility
 * with existing code that imports VesselPickerField or VesselPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Vessel-specific configuration ────────────────────────────────────────────
export const VESSEL_CONFIG = {
	apiEndpoint: "/api/Vessel/active",
	entityName: "Vessel",
	idField: "id",
	displayFields: {
		primary: "code",
		secondary: "name"
	},
	tableHeaders: ["Vessel Code", "Vessel Name"],
	tableAccessors: ["code", "name"],
	searchFields: [
		{ label: "All", field: "all", index: 0 },
		{ label: "Vessel Name", field: "name", index: 1 },
		{ label: "Vessel Code", field: "code", index: 2 },
	],
	placeholder: "--Select vessel--"
};

// ── VesselPickerField wrapper ────────────────────────────────────────────────
/**
 * VesselPickerField
 * 
 * Usage:
 * <VesselPickerField
 *   label="Vessel Code"
 *   vesselId={form.vesselId}
 *   displayValue={form.vesselCode}
 *   onSelect={(vessel) => {
 *     set("vesselId", vessel.id);
 *     set("vesselCode", vessel.code);
 *     set("vesselName", vessel.name);
 *   }}
 *   onClear={() => {
 *     set("vesselId", "");
 *     set("vesselCode", "");
 *     set("vesselName", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function VesselPickerField({
	vesselId,           // vessel-specific prop name
	displayValue = "",
	onSelect,
	onClear,
	...rest             // label, isRequired, error, disabled, etc.
}) {
	return (
		<EntityPickerField
			{...rest}
			config={VESSEL_CONFIG}
			entityId={vesselId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── VesselPickerModal wrapper ────────────────────────────────────────────────
/**
 * VesselPickerModal
 * 
 * Usage:
 * <VesselPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(vessel) => {
 *     set("vesselId", vessel.id);
 *     set("vesselCode", vessel.code);
 *   }}
 * />
 */
export function VesselPickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={VESSEL_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { VesselPickerField as default };
